import React from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { usePropertyField, useSlotGroup, useContentToken } from '@vibesites/runtime/react';
import { RichTextContent, EditableSlotGroup, EditableToken } from '@vibesites/runtime/react';
import { PetsAllowedIcon } from '@vibesites/assets';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

function hasValue(val: unknown): val is string {
  if (val === null || val === undefined) return false;
  const s = String(val).trim().toLowerCase();
  return (
    s !== '' &&
    s !== 'false' &&
    s !== 'null' &&
    s !== 'undefined' &&
    s !== '0' &&
    s !== 'none' &&
    s !== 'n/a' &&
    s !== 'na'
  );
}


function isTruthy(val: unknown): boolean {
  if (val === true) return true;
  if (!val) return false;
  const s = String(val).trim().toLowerCase();
  return s === 'true' || s === 'yes' || s === '1';
}


// ============================================================================
// Slot group definitions
// ============================================================================

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url:     { label: 'URL', type: 'url', defaultValue: '/amenities' },
    label:   { label: 'Link Text', type: 'text', defaultValue: 'View Amenities' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function PetPolicy() {
  const allowsCats = usePropertyField('property_allows_cats');
  const allowsDogs = usePropertyField('property_allows_dogs');
  const maxPets = usePropertyField('property_max_pets') as string | null;
  const maxWeight = usePropertyField('property_max_pet_weight_limit') as string | null;
  const petDeposit = usePropertyField('property_pet_deposit') as string | null;
  const petFee = usePropertyField('property_upfront_pet_fee') as string | null;
  const monthlyRent = usePropertyField('property_monthly_pet_rent') as string | null;
  const restrictions = usePropertyField('property_pet_restrictions') as string | null;
  const policyDescription = usePropertyField('property_pet_policy_description') as string | null;
  const policyInfo = usePropertyField('property_petpolicy_info') as string | null;

  const ctaLink = useSlotGroup('petpolicy.cta_link', ctaLinkDef);

  // Price tiles are split into a prefix (currency symbol) and a value bound
  // to the property field — both plain text so they render inline as spans
  // and inherit the parent .pet-stat-value typography directly.
  const petDepositPrefix = useContentToken('petpolicy.stat_pet_deposit_prefix');
  const petDepositValueText = useContentToken('petpolicy.stat_pet_deposit_value');
  const petFeePrefix = useContentToken('petpolicy.stat_pet_fee_prefix');
  const petFeeValueText = useContentToken('petpolicy.stat_pet_fee_value');
  const monthlyRentPrefix = useContentToken('petpolicy.stat_monthly_rent_prefix');
  const monthlyRentValueText = useContentToken('petpolicy.stat_monthly_rent_value');
  const monthlyRentSuffix = useContentToken('petpolicy.stat_monthly_rent_suffix');
  // Max weight tile uses the same split — value bound to the field, suffix
  // (e.g. "lbs") as a separate editable text token so styling inherits.
  const maxWeightValueText = useContentToken('petpolicy.stat_max_weight_value');
  const maxWeightSuffix = useContentToken('petpolicy.stat_max_weight_suffix');

  const catsDogsList = [
    isTruthy(allowsDogs) && 'Dogs',
    isTruthy(allowsCats) && 'Cats',
  ].filter(Boolean);

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  // Show the welcome badge when the property allows at least one pet type.
  const showWelcomeBadge = catsDogsList.length > 0;

  const descriptionHtml = policyDescription || policyInfo || null;

  // Gate the whole stats column — only renders when at least one field has
  // a real, non-zero value. Individual cards are gated the same way.
  const hasPetData = hasValue(maxPets) || hasValue(maxWeight) || hasValue(petDeposit) || hasValue(petFee) || hasValue(monthlyRent);

  return (
    <section ref={sectionRef} className="reveal-fade-up py-12 md:py-20 bg-[var(--vibe-color-background)]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="pet-layout">
          {/* Left column: description + restrictions */}
          <div className="pet-content">
            {/* Pets welcome badge */}
            {showWelcomeBadge && (
              <div className="flex items-center gap-3 mb-6">
                <PetsAllowedIcon size={28} color="var(--vibe-color-primary)" />
                <RichTextContent
                  path="petpolicy.welcome_text"
                  as="div"
                  className="font-heading text-lg text-[var(--vibe-color-text)] font-medium tracking-wide"
                />
              </div>
            )}

            {/* Policy description */}
            {descriptionHtml && (
              <RichTextContent
                path="petpolicy.description"
                label="Pet Policy Description"
                defaultField="property_pet_policy_description"
                as="div"
                className="font-body text-[0.95rem] leading-relaxed text-[var(--vibe-color-text)] mb-6 [&_p]:mb-3 [&_ul]:list-none [&_ul]:pl-0 [&_ul]:mb-3 [&_li]:relative [&_li]:pl-5 [&_li]:mb-1 [&_li]:before:content-[''] [&_li]:before:absolute [&_li]:before:left-1 [&_li]:before:top-1/2 [&_li]:before:-translate-y-1/2 [&_li]:before:w-1.5 [&_li]:before:h-1.5 [&_li]:before:rounded-full [&_li]:before:bg-current"
              />
            )}

            {/* Pet restrictions */}
            {hasValue(restrictions) && (
              <div className="pet-restrictions">
                <RichTextContent
                  path="petpolicy.restrictions_heading"
                  as="div"
                  className="font-heading text-base font-medium text-[var(--vibe-color-text)] mb-3 tracking-wide"
                />
                <RichTextContent
                  path="petpolicy.restrictions"
                  label="Pet Restrictions"
                  defaultField="property_pet_restrictions"
                  as="div"
                  className="font-body text-sm leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-2 [&_ul]:list-none [&_ul]:pl-0 [&_ul]:mb-2 [&_li]:relative [&_li]:pl-5 [&_li]:mb-0.5 [&_li]:before:content-[''] [&_li]:before:absolute [&_li]:before:left-1 [&_li]:before:top-1/2 [&_li]:before:-translate-y-1/2 [&_li]:before:w-1.5 [&_li]:before:h-1.5 [&_li]:before:rounded-full [&_li]:before:bg-current"
                />
              </div>
            )}

            {/* CTA */}
            <div className="mt-8">
              <EditableSlotGroup groupPath="petpolicy.cta_link" definition={ctaLinkDef}>
                <a
                  href={ctaLink.url || '/amenities'}
                  target={ctaLink._target || '_self'}
                  className="pet-cta"
                >
                  {ctaLink.label || 'View Amenities'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>

          {/* Right column: each card only mounts when its backing property
              field has a real, non-zero value — no guesswork shown to visitors.
              EditableToken wrappers stay so cards are selectable when visible. */}
          {hasPetData && (
            <div className="pet-stats">
              {hasValue(maxPets) && (
                <div className="pet-stat-card">
                  <RichTextContent path="petpolicy.stat_max_pets_label" label="Max Pets Card Label" as="div" className="pet-stat-label" />
                  <RichTextContent path="petpolicy.stat_max_pets_value" label="Max Pets Value" as="div" className="pet-stat-value" />
                </div>
              )}
              {hasValue(maxWeight) && (
                <div className="pet-stat-card">
                  <RichTextContent path="petpolicy.stat_max_weight_label" label="Max Weight Card Label" as="div" className="pet-stat-label" />
                  <div className="pet-stat-value">
                    <EditableToken path="petpolicy.stat_max_weight_value" as="span" label="Max Weight Value">
                      {maxWeightValueText}
                    </EditableToken>
                    {' '}
                    <EditableToken path="petpolicy.stat_max_weight_suffix" as="span" label="Max Weight Suffix">
                      {maxWeightSuffix}
                    </EditableToken>
                  </div>
                </div>
              )}
              {hasValue(petDeposit) && (
                <div className="pet-stat-card">
                  <RichTextContent path="petpolicy.stat_pet_deposit_label" label="Pet Deposit Card Label" as="div" className="pet-stat-label" />
                  <div className="pet-stat-value">
                    <EditableToken path="petpolicy.stat_pet_deposit_prefix" as="span" label="Pet Deposit Prefix">
                      {petDepositPrefix}
                    </EditableToken>
                    <EditableToken path="petpolicy.stat_pet_deposit_value" as="span" label="Pet Deposit Value">
                      {petDepositValueText}
                    </EditableToken>
                  </div>
                  <RichTextContent path="petpolicy.stat_pet_deposit_sublabel" label="Pet Deposit Sublabel" as="div" className="pet-stat-sublabel" />
                </div>
              )}
              {hasValue(petFee) && (
                <div className="pet-stat-card">
                  <RichTextContent path="petpolicy.stat_pet_fee_label" label="Pet Fee Card Label" as="div" className="pet-stat-label" />
                  <div className="pet-stat-value">
                    <EditableToken path="petpolicy.stat_pet_fee_prefix" as="span" label="Pet Fee Prefix">
                      {petFeePrefix}
                    </EditableToken>
                    <EditableToken path="petpolicy.stat_pet_fee_value" as="span" label="Pet Fee Value">
                      {petFeeValueText}
                    </EditableToken>
                  </div>
                  <RichTextContent path="petpolicy.stat_pet_fee_sublabel" label="Pet Fee Sublabel" as="div" className="pet-stat-sublabel" />
                </div>
              )}
              {hasValue(monthlyRent) && (
                <div className="pet-stat-card">
                  <RichTextContent path="petpolicy.stat_monthly_rent_label" label="Monthly Pet Rent Card Label" as="div" className="pet-stat-label" />
                  <div className="pet-stat-value">
                    <EditableToken path="petpolicy.stat_monthly_rent_prefix" as="span" label="Monthly Pet Rent Prefix">
                      {monthlyRentPrefix}
                    </EditableToken>
                    <EditableToken path="petpolicy.stat_monthly_rent_value" as="span" label="Monthly Pet Rent Value">
                      {monthlyRentValueText}
                    </EditableToken>
                    <EditableToken path="petpolicy.stat_monthly_rent_suffix" as="span" label="Monthly Pet Rent Suffix">
                      {monthlyRentSuffix}
                    </EditableToken>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`
        .pet-layout {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2.5rem;
        }
        @media (min-width: 768px) {
          .pet-layout {
            grid-template-columns: 1fr 1fr;
            gap: 3rem;
            align-items: start;
          }
        }

        .pet-content {
          order: 2;
        }
        @media (min-width: 768px) {
          .pet-content {
            order: 1;
          }
        }

        .pet-stats {
          order: 1;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.75rem;
        }
        @media (min-width: 768px) {
          .pet-stats {
            order: 2;
          }
        }

        .pet-stat-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 1.25rem 1rem;
          background: #f8f9fa;
          border: 1px solid var(--vibe-color-border);
          border-radius: 4px;
          min-height: 100px;
        }

        .pet-stat-label {
          font-family: var(--vibe-font-heading);
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--vibe-color-text);
          margin-bottom: 0.5rem;
        }
        .pet-stat-label p { margin: 0; }

        .pet-stat-value {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text-muted);
        }
        .pet-stat-value p { margin: 0; }

        .pet-stat-sublabel {
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          color: var(--vibe-color-text-muted);
          opacity: 0.7;
          margin-top: 0.15rem;
        }
        .pet-stat-sublabel p { margin: 0; }

        .pet-restrictions {
          padding: 1rem 1.25rem;
          border-left: 3px solid var(--vibe-color-primary);
          margin-bottom: 0.5rem;
        }

        .pet-cta {
          display: inline-block;
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: var(--vibe-color-text-on-primary);
          background: var(--vibe-color-primary);
          padding: 0.75rem 2rem;
          border-radius: 2px;
          text-decoration: none;
          transition: opacity 0.2s;
        }
        .pet-cta:hover {
          opacity: 0.85;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PetPolicy',
  displayName: 'Pet Policy',
  tokens: {
    'petpolicy.welcome_text': {
      label: 'Pets Welcome Text',
      type: 'richtext',
      description:
        'Badge text shown next to the paws icon.',
      defaultValue: '<p>Dogs &amp; Cats Welcome</p>',
    },
    'petpolicy.description': {
      label: 'Pet Policy Description',
      type: 'richtext',
      defaultField: 'property_pet_policy_description',
      suggestedFields: ['property_pet_policy_description', 'property_petpolicy_info'],
    },
    'petpolicy.restrictions_heading': {
      label: 'Pet Restrictions Heading',
      type: 'richtext',
      description: 'Heading shown above the pet restrictions list.',
      defaultValue: '<p>Pet Restrictions</p>',
    },
    'petpolicy.restrictions': {
      label: 'Pet Restrictions',
      type: 'richtext',
      defaultField: 'property_pet_restrictions',
    },
    // Stat card labels — richtext so authors can apply bold/italic formatting.
    // Cards only mount when hasValue() passes on the backing field, so these
    // labels can never appear without real data.
    'petpolicy.stat_max_pets_label': {
      label: 'Max Pets Card Label',
      type: 'richtext',
      description: 'Override the "Pets Per Household" heading on this card.',
      defaultValue: '<p>Pets Per Household</p>',
    },
    'petpolicy.stat_max_weight_label': {
      label: 'Max Weight Card Label',
      type: 'richtext',
      description: 'Override the "Max Weight" heading on this card.',
      defaultValue: '<p>Max Weight</p>',
    },
    'petpolicy.stat_pet_deposit_label': {
      label: 'Pet Deposit Card Label',
      type: 'richtext',
      description: 'Override the "Pet Deposit" heading on this card.',
      defaultValue: '<p>Pet Deposit</p>',
    },
    'petpolicy.stat_pet_deposit_sublabel': {
      label: 'Pet Deposit Sublabel',
      type: 'richtext',
      description: 'Override the "(Refundable)" note below the deposit value.',
      defaultValue: '<p>(Refundable)</p>',
    },
    'petpolicy.stat_pet_fee_label': {
      label: 'Pet Fee Card Label',
      type: 'richtext',
      description: 'Override the "Pet Fee" heading on this card.',
      defaultValue: '<p>Pet Fee</p>',
    },
    'petpolicy.stat_pet_fee_sublabel': {
      label: 'Pet Fee Sublabel',
      type: 'richtext',
      description: 'Override the "(Non-Refundable)" note below the fee value.',
      defaultValue: '<p>(Non-Refundable)</p>',
    },
    'petpolicy.stat_monthly_rent_label': {
      label: 'Monthly Pet Rent Card Label',
      type: 'richtext',
      description: 'Override the "Monthly Pet Rent" heading on this card.',
      defaultValue: '<p>Monthly Pet Rent</p>',
    },
    // Stat card values — richtext so authors can apply inline formatting.
    // defaultField binds directly to the live property field; the hasValue()
    // gate on the card container is the primary guard against empty cards.
    'petpolicy.stat_max_pets_value': {
      label: 'Max Pets Value',
      type: 'richtext',
      description: 'Override the displayed value. Defaults to the property\'s max pets field.',
      defaultField: 'property_max_pets',
    },
    'petpolicy.stat_max_weight_value': {
      label: 'Max Weight Value',
      type: 'text',
      description: 'Override the displayed value. Defaults to the property\'s weight limit field.',
      defaultField: 'property_max_pet_weight_limit',
    },
    'petpolicy.stat_max_weight_suffix': {
      label: 'Max Weight Suffix',
      type: 'text',
      description: 'Unit shown after the weight value (e.g. "lbs", "kg").',
      defaultValue: 'lbs',
    },
    // Price tiles split prefix and value into separate text tokens so both
    // render inline (as <span>) and inherit the parent .pet-stat-value
    // typography directly — no <p> block in between.
    'petpolicy.stat_pet_deposit_prefix': {
      label: 'Pet Deposit Prefix',
      type: 'text',
      description: 'Currency or unit symbol shown before the pet deposit value.',
      defaultValue: '$',
    },
    'petpolicy.stat_pet_deposit_value': {
      label: 'Pet Deposit Value',
      type: 'text',
      description: 'Override the displayed value. Defaults to the property\'s pet deposit field.',
      defaultField: 'property_pet_deposit',
    },
    'petpolicy.stat_pet_fee_prefix': {
      label: 'Pet Fee Prefix',
      type: 'text',
      description: 'Currency or unit symbol shown before the pet fee value.',
      defaultValue: '$',
    },
    'petpolicy.stat_pet_fee_value': {
      label: 'Pet Fee Value',
      type: 'text',
      description: 'Override the displayed value. Defaults to the property\'s upfront pet fee field.',
      defaultField: 'property_upfront_pet_fee',
    },
    'petpolicy.stat_monthly_rent_prefix': {
      label: 'Monthly Pet Rent Prefix',
      type: 'text',
      description: 'Currency or unit symbol shown before the monthly pet rent value.',
      defaultValue: '$',
    },
    'petpolicy.stat_monthly_rent_value': {
      label: 'Monthly Pet Rent Value',
      type: 'text',
      description: 'Override the displayed value. Defaults to the property\'s monthly pet rent field.',
      defaultField: 'property_monthly_pet_rent',
    },
    'petpolicy.stat_monthly_rent_suffix': {
      label: 'Monthly Pet Rent Suffix',
      type: 'text',
      description: 'Unit shown after the monthly pet rent value (e.g. "/mo", "/month").',
      defaultValue: '/mo',
    },
  },
  slotGroups: {
    'petpolicy.cta_link': {
      label: 'CTA Link',
      fields: {
        url:     { label: 'URL', type: 'url', defaultValue: '/amenities' },
        label:   { label: 'Link Text', type: 'text', defaultValue: 'View Amenities' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
};

(PetPolicy as any).contentSchema = contentSchema;

export default PetPolicy;
